// ModelAllocationModal — what a managed model holds, as its own bar / treemap of member
// funds, opened by clicking the model's bar or tile in the allocation chart. A plan with
// several models gets a tab strip to switch between them without leaving the dialog.
// Hovering a fund shows FundTip (weight in the model, set by the plan advisor); clicking
// one opens its fact sheet ON TOP — the page keeps this dialog open underneath, so
// closing the sheet lands back on the same model, tab and view.
//
// Presentational and controlled: the page owns which model is active (`activeKey`) and
// gets `onChangeModel` from the tabs; the only local state is the bar/map view.
import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Modal, BottomSheet, BarChart, Treemap, Dropdown } from '../../../reusable';
import { cx } from '../../../../utils/cx';
import { useMediaQuery } from '../../../../hooks';
import { formatCurrency } from '../../../../utils/format';
import { CloseIcon, CaretIcon, CheckIcon } from '../../../icons/Icons';
import { countLabel } from '../portfolio.adapters';
import { FundTip } from './FundTip';
import { ModelTag } from './ModelRow';
import { VizModeToggle } from './VizModeToggle';
import { niceAxis, fmtTick } from './vizScale';
import './model-allocation-modal.css';

const TITLE_ID = 'model-alloc-title';

function ModelAllocationContent({ models, active, onChangeModel, onPickFund, onClose, plan, defaultMode }) {
  const { t } = useTranslation();
  const [mode, setMode] = useState(defaultMode);

  const children = useMemo(
    () => [...(active.children || [])].sort((a, b) => (b.balance || 0) - (a.balance || 0)),
    [active],
  );
  const childByKey = useMemo(() => Object.fromEntries(children.map((c) => [c.key ?? c.ticker, c])), [children]);
  const { max, ticks } = useMemo(() => niceAxis(Math.max(0, ...children.map((c) => c.balance || 0))), [children]);

  const barItems = useMemo(
    () => children.map((c) => ({
      key: c.key ?? c.ticker,
      label: c.ticker,
      value: c.balance,
      topLabel: c.weightLabel ?? (c.weightInModel != null ? `${c.weightInModel}%` : undefined),
      color: c.color,
    })),
    [children],
  );
  const mapItems = useMemo(
    () => children.map((c) => ({
      key: c.key ?? c.ticker,
      value: c.weightInModel ?? c.balancePct ?? 0,
      color: c.color,
      label: c.ticker,
      name: (c.name || '').replace('Vanguard ', ''),
      sub: formatCurrency(c.balance),
    })),
    [children],
  );

  // No `_parent` on the child: it already carries `isModelChild`, so FundTip states the
  // weight in the model and its footer names the click target (bar / tile), which here
  // really does open the fund's fact sheet.
  const renderTip = (item) => (
    <FundTip fund={childByKey[item.key]} plan={plan} surface={mode === 'bar' ? 'bar' : 'tile'} />
  );
  const pick = (item) => {
    const c = childByKey[item.key];
    if (c) onPickFund?.(c.ticker);
  };

  const whole = (v) => (v == null ? v : Math.round(v));
  // The share By Fund prints for the model (FM-3056).
  const shareOf = (m) => m.displayPct ?? whole(m.balancePct ?? m.pct);
  const pct = shareOf(active);
  const multi = models.length > 1;

  // One model: the title is plain text. Several: the title IS the picker — a dropdown
  // showing the current model, whose menu lists the others with their colour, share
  // and fund count — so switching never means closing and reopening.
  const title = multi ? (
    <Dropdown
      align="left"
      portal
      className="model-alloc__picker"
      menuClassName="model-alloc__menu"
      trigger={({ open, toggle }) => (
        <button
          type="button"
          id={TITLE_ID}
          className={cx('model-alloc__pick', open && 'is-open')}
          onClick={toggle}
          aria-haspopup="listbox"
          aria-expanded={open}
          aria-label={t('portfolio.modelAlloc.switchLabel', { count: models.length })}
        >
          <span className="model-alloc__pick-nm">{active.name}</span>
          <ModelTag t={t} />
          <CaretIcon className="model-alloc__pick-caret" />
        </button>
      )}
    >
      {({ close }) => (
        <ul className="model-alloc__options" role="listbox" aria-label={t('portfolio.modelAlloc.switchLabel', { count: models.length })}>
          {models.map((m) => {
            const on = m.key === active.key;
            return (
              <li key={m.key}>
                <button
                  type="button"
                  role="option"
                  aria-selected={on}
                  className={cx('model-alloc__option', on && 'is-active')}
                  onClick={() => {
                    onChangeModel?.(m.key);
                    close();
                  }}
                >
                  <span className="model-alloc__option-sw" style={{ background: m.color }} />
                  <span className="model-alloc__option-nm">{m.name}</span>
                  <span className="model-alloc__option-meta">
                    {shareOf(m) != null && <strong>{shareOf(m)}%</strong>}
                    {t('portfolio.viz.modelSub', { count: (m.children || []).length })}
                  </span>
                  {on && <CheckIcon className="model-alloc__option-check" />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </Dropdown>
  ) : (
    <>
      {active.name} <ModelTag t={t} />
    </>
  );

  return (
    <div className="model-alloc">
      <header className="model-alloc__head">
        <span className="model-alloc__sw" style={{ background: active.color }} />
        <div className="model-alloc__id">
          <div className="model-alloc__eyebrow">{t('portfolio.modelAlloc.title')}</div>
          <h2 id={multi ? undefined : TITLE_ID} className="model-alloc__title">{title}</h2>
          {multi && (
            <span className="model-alloc__pick-hint">
              {t('portfolio.modelAlloc.pickHint', { count: models.length })}
            </span>
          )}
          <div className="model-alloc__sub">
            {active.ticker && <span className="model-alloc__tkr">{active.ticker}</span>}
            <span>{formatCurrency(active.balance || 0)}</span>
            {pct != null && <span>{t('portfolio.modelAlloc.ofPortfolio', { pct })}</span>}
            <span>{countLabel(children, t)}</span>
          </div>
        </div>
        <button type="button" className="model-alloc__close" onClick={onClose} aria-label={t('portfolio.fundDetail.close')}>
          <CloseIcon />
        </button>
      </header>

      <div
        className="model-alloc__panel"
        role="region"
        id={`panel-${active.key}`}
        aria-labelledby={TITLE_ID}
      >
        {children.length ? (
          <>
            <div className="model-alloc__bar">
              <span className="model-alloc__what">
                {mode === 'bar' ? t('portfolio.viz.sortedByBalance') : t('portfolio.modelAlloc.sizedByWeight')}
              </span>
              <VizModeToggle mode={mode} onChange={setMode} />
            </div>
            <div className="model-alloc__stage">
              {mode === 'bar' ? (
                <BarChart
                  items={barItems}
                  maxValue={max}
                  ticks={ticks}
                  formatTick={fmtTick}
                  onSelect={pick}
                  renderTip={renderTip}
                  minBandWidth={36}
                  ariaLabel={t('portfolio.modelAlloc.barChartAria', { name: active.name })}
                />
              ) : (
                <Treemap
                  items={mapItems}
                  onSelect={pick}
                  renderTip={renderTip}
                  ariaLabel={t('portfolio.modelAlloc.treemapAria', { name: active.name })}
                />
              )}
            </div>
            <p className="model-alloc__hint">{t('portfolio.modelAlloc.hint')}</p>
          </>
        ) : (
          // Not reachable from the holdings tree today (a model with nothing held is
          // dropped there), kept so a caller passing declared models still gets a page.
          <div className="model-alloc__empty">
            <p className="model-alloc__empty-title">{t('portfolio.modelAlloc.emptyTitle')}</p>
            {(active.lineup || []).length > 0 && (
              <>
                <p className="model-alloc__empty-note">{t('portfolio.modelAlloc.emptyNote')}</p>
                <ul className="model-alloc__lineup">
                  {active.lineup.map((f) => (
                    <li key={f.ticker}>
                      <span className="model-alloc__lineup-nm">{f.name || f.ticker}</span>
                      <span className="model-alloc__lineup-tkr">{f.ticker}</span>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const modelShape = PropTypes.shape({
  key: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  ticker: PropTypes.string,
  color: PropTypes.string,
  balance: PropTypes.number,
  balancePct: PropTypes.number,
  pct: PropTypes.number,
  children: PropTypes.array,
  lineup: PropTypes.array,
});

ModelAllocationContent.propTypes = {
  models: PropTypes.arrayOf(modelShape).isRequired,
  active: modelShape.isRequired,
  onChangeModel: PropTypes.func,
  onPickFund: PropTypes.func,
  onClose: PropTypes.func,
  plan: PropTypes.object,
  defaultMode: PropTypes.oneOf(['bar', 'map']),
};

export function ModelAllocationModal({
  open, models = [], activeKey, onChangeModel, onPickFund, onClose, plan, defaultMode = 'bar',
}) {
  // Bottom sheet under 600px, centred dialog above — the same split FundDetailModal makes.
  const mobile = useMediaQuery('(max-width: 600px)');
  const active = models.find((m) => m.key === activeKey) || null;
  const content = open && active ? (
    <ModelAllocationContent
      models={models}
      active={active}
      onChangeModel={onChangeModel}
      onPickFund={onPickFund}
      onClose={onClose}
      plan={plan}
      defaultMode={defaultMode}
    />
  ) : null;
  if (mobile) {
    return <BottomSheet open={open && !!active} onClose={onClose} flush>{content}</BottomSheet>;
  }
  return (
    <Modal open={open && !!active} onClose={onClose} size="xl" flush ariaLabelledBy={TITLE_ID}>
      {content}
    </Modal>
  );
}

ModelAllocationModal.propTypes = {
  open: PropTypes.bool,
  /** The holdings tree's model items (adapter `buildHoldingsTree`, `type === 'model'`). */
  models: PropTypes.arrayOf(modelShape),
  /** Which model's tab is showing — the page owns it. */
  activeKey: PropTypes.string,
  onChangeModel: PropTypes.func,
  /** A member fund was clicked — open its fact sheet (this dialog stays open). */
  onPickFund: PropTypes.func,
  onClose: PropTypes.func,
  /** Plan record — FundTip's Future-election row asks it whether elections apply. */
  plan: PropTypes.object,
  defaultMode: PropTypes.oneOf(['bar', 'map']),
};

export default ModelAllocationModal;
